// Looking at a document without leaving the app.
//
// PDFs and images render here; Word and Excel do not, because the browser
// cannot show them and writing an Office viewer is not this product's job —
// they get a download button and open in whatever the device already has
// (spec: 02-jobs.md, 06-library.md).
//
// The PDF goes in an <object>, which uses the browser's own PDF viewer. It
// brings zoom, search and print for free, and it means no third-party PDF
// library shipped to every worker's phone — but only where the browser HAS
// one. Phone browsers don't: iOS Safari paints an embedded PDF as a frozen
// picture of page one and Android Chrome won't embed at all, so anywhere
// `navigator.pdfViewerEnabled` doesn't say true, PdfPages renders the pages
// itself with pdf.js instead (lazy-loaded — desktop never downloads it).
// The split itself lives in PdfViewer.tsx's PdfFrame, shared with the form
// submission page and the timesheet PDF.
import { contentUrl, type Attachment } from '../documentsApi';
import { DownloadLink } from './DownloadLink';
import { PdfFrame } from './PdfViewer';

export function DocumentViewer({
  attachment,
  onClose,
}: {
  attachment: Attachment;
  onClose: () => void;
}) {
  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="modal viewer"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={attachment.title}
      >
        {/* Margin on the row, none on the heading — without it the Download
            and Close buttons sit hard on the image or PDF below them. */}
        <div className="row mb-5">
          <h1 className="!mb-0">{attachment.title}</h1>
          <span className="spacer" />
          <DownloadLink href={contentUrl(attachment.id, { download: true })} className="btn-secondary">
            Download
          </DownloadLink>
          <button className="secondary" onClick={onClose}>
            Close
          </button>
        </div>

        {attachment.description && <p className="muted">{attachment.description}</p>}

        {attachment.viewer === 'image' && (
          <img className="viewer-image" src={contentUrl(attachment.id)} alt={attachment.title} />
        )}

        {attachment.viewer === 'pdf' && (
          <PdfFrame
            src={contentUrl(attachment.id)}
            downloadHref={contentUrl(attachment.id, { download: true })}
            label={attachment.title}
          />
        )}

        {attachment.viewer === 'download' && (
          <p className="muted">
            Word and Excel files open in the app you already use for them.{' '}
            <DownloadLink href={contentUrl(attachment.id, { download: true })}>Download this one</DownloadLink>.
          </p>
        )}
      </div>
    </div>
  );
}
